import { html, unsafeSVG, customElement, state } from '@oskecare/fe-utilities/lib/LitElement';
import OrangeL2Widget from '@oskecare/fe-utilities/lib/OrangeL2Widget';
import '@oskecare/ui-library/components/pageLoader';
import '@oskecare/ui-library/components/error';
import '@oskecare/ui-library/components/input';
import trashIcon from '@oskecare/orange-design-system/src/assets/icons/trash.svg';
import saveIcon from '@oskecare/orange-design-system/src/assets/icons/save.svg';
import warningIcon from '@oskecare/orange-design-system/src/assets/icons/warning-important.svg';
import { TYPE_ERROR } from '@oskecare/ui-library/components/message';

const METHOD_GET = 'GET';
const METHOD_POST = 'POST';
const METHOD_PATCH = 'PATCH';
const METHOD_PUT = 'PUT';
const METHOD_DELETE = 'DELETE';
const METHODS = [METHOD_GET, METHOD_POST, METHOD_PATCH, METHOD_PUT, METHOD_DELETE];
const METHODS_WITH_BODY = [METHOD_POST, METHOD_PATCH, METHOD_PUT, METHOD_DELETE];

const LOCAL_STORAGE_SAVE_PREFIX = 'ec2fe-api-tester-save';

@customElement('osk-api-tester')
class ApiTester extends OrangeL2Widget {
  /**
   * @type {string}
   */
  logSource = 'api-tester';

  /**
   * @type {string}
   */
  @state()
  accessor _url = '';

  /**
   * @type {string}
   */
  @state()
  accessor _method = 'GET';

  /**
   * @type {string}
   */
  @state()
  accessor _body = '';

  /**
   * @type {?CustomerData}
   */
  @state()
  accessor _customerData = null;

  /**
   * @type {*}
   */
  @state()
  accessor _response;

  /**
   * @type {boolean}
   */
  @state()
  accessor _hasError = false;

  /**
   * @type {boolean}
   */
  @state()
  accessor _useCustomHeaders = false;

  /**
   * @type {object}
   */
  @state()
  accessor _headers = {
    Accept: 'application/json; charset=utf-8',
    'Content-Type': 'application/json; charset=utf-8',
  };

  /**
   * @type {string}
   */
  @state()
  accessor _newHeader = '';

  /**
   * @type {string}
   */
  @state()
  accessor _saveName = '';

  /**
   * @type {string[]}
   */
  @state()
  accessor _saves = [];

  /**
   * @type {string}
   */
  @state()
  accessor _downloadFileName = '';

  /**
   * @returns {string}
   */
  get workingSn() {
    return this.infrastructure.customerDataManager.workingSn;
  }

  /**
   * @returns {SnData}
   */
  get workingSnData() {
    return this._customerData.getSnDataBySn(this.workingSn);
  }

  /**
   * @return {CnData}
   */
  get cnData() {
    return this._customerData.cnData;
  }

  /**
   * @returns {object}
   */
  get substitutions() {
    return {
      cn: this.cnData.cn,
      billingAccountId: this.cnData.billingAccountId,
      customerId: this.cnData.customerId,
      sn: this.workingSn,
      snWithZero: this.workingSn.replace('421', '0'),
      subscriptionId: this.workingSnData.subscriptionId,
    };
  }

  /**
   * @returns {string}
   */
  get substitutedUrl() {
    let url = this._url;

    Object.entries(this.substitutions).forEach(([key, value]) => {
      url = url.replaceAll(`{${key}}`, value);
    });

    return url;
  }

  /**
   * @returns {boolean}
   */
  get isUrlValid() {
    return URL.canParse(this.substitutedUrl);
  }

  /**
   * @returns {boolean}
   */
  get isBodyAvailable() {
    return METHODS_WITH_BODY.includes(this._method);
  }

  /**
   * @returns {*}
   */
  get bodyJson() {
    try {
      return JSON.parse(this._body);
    } catch {
      return null;
    }
  }

  /**
   * @returns {boolean}
   */
  get isBodyValid() {
    return !this._body || this.bodyJson !== null;
  }

  /**
   * @returns {string}
   */
  get responseString() {
    if (typeof this._response === 'string') {
      return this._response;
    }
    return JSON.stringify(this._response, null, 2);
  }

  /**
   * @return {boolean}
   */
  get isNewHeaderNameValid() {
    return /^[\w-]+$/.test(this._newHeader);
  }

  /**
   * @return {string[]}
   */
  static getSaves() {
    const savesString = localStorage.getItem(`${LOCAL_STORAGE_SAVE_PREFIX}-list`);
    if (!savesString) {
      return [];
    }

    const saves = JSON.parse(savesString);
    if (!Array.isArray(saves)) {
      return [];
    }

    return saves;
  }

  /**
   * @param {string} saveName
   */
  saveState(saveName) {
    localStorage.setItem(
      `${LOCAL_STORAGE_SAVE_PREFIX}-${saveName}`,
      JSON.stringify({
        method: this._method,
        useCustomHeaders: this._useCustomHeaders,
        headers: this._headers,
        body: this._body,
        url: this._url,
      }),
    );
    if (!this._saves.includes(saveName)) {
      this._saves.push(saveName);
      localStorage.setItem(`${LOCAL_STORAGE_SAVE_PREFIX}-list`, JSON.stringify(this._saves));
    }
  }

  /**
   * @param {string} saveName
   */
  loadState(saveName) {
    const data = JSON.parse(localStorage.getItem(`${LOCAL_STORAGE_SAVE_PREFIX}-${saveName}`));
    this._method = data.method;
    this._useCustomHeaders = data.useCustomHeaders;
    this._headers = data.headers;
    this._body = data.body;
    this._url = data.url;
  }

  /**
   * @param {string} saveName
   */
  deleteSave(saveName) {
    localStorage.removeItem(`${LOCAL_STORAGE_SAVE_PREFIX}-${saveName}`);
    this._saves = this._saves.filter(save => save !== saveName);
    localStorage.setItem(`${LOCAL_STORAGE_SAVE_PREFIX}-list`, JSON.stringify(this._saves));
  }

  /**
   * @see OrangeL2Widget.getData
   * @returns {Promise<void>}
   */
  async getData() {
    await super.getData({
      fetch: async () => {
        this._customerData = await this.infrastructure.customerDataManager.getCustomerData();
        this._saves = ApiTester.getSaves();
      },
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async handleTestClick() {
    if (!this.isUrlValid || !this.isBodyValid) {
      return;
    }
    this._response = 'LOADING...';
    this._hasError = false;
    try {
      this._response = await this.request({
        method: this._method,
        route: this.substitutedUrl,
        body: this.isBodyAvailable && this.isBodyValid ? this.bodyJson : null,
        useDefaultHeaders: !this._useCustomHeaders,
        headers: this._useCustomHeaders ? this._headers : null,
      });
    } catch (error) {
      this._response = error.message;
      if (!error.requestLogData?.response) {
        this._response += ' (Check console for CORS issues)';
      }
      if (error.responseBody) {
        this._response += `\n\n${JSON.stringify(error.responseBody, null, 2)}`;
      }
      this._hasError = true;
    }
  }

  /**
   * @param {CustomEvent} event
   */
  handleUrlChange(event) {
    this._url = event.detail.value;
  }

  /**
   * @param {string} method
   * @returns {function(Event): void}
   */
  getHandleMethodChange(method) {
    return () => {
      this._method = method;
    };
  }

  /**
   * @param {Event} event
   */
  handleBodyChange(event) {
    this._body = event.target.value;
  }

  /**
   * @param {CustomEvent} event
   */
  handleUseCustomHeadersChange(event) {
    this._useCustomHeaders = event.detail.value;
  }

  /**
   * @param {string} header
   * @return {function(CustomEvent): void}
   */
  getHandleHeaderChange(header) {
    return event => {
      const headers = { ...this._headers };
      headers[header] = event.detail.value;
      this._headers = headers;
    };
  }

  /**
   * @param {string} header
   * @return {function(): void}
   */
  getHandleRemoveHeader(header) {
    return () => {
      const headers = { ...this._headers };
      delete headers[header];
      this._headers = headers;
    };
  }

  /**
   * @param {CustomEvent} event
   */
  handleNewHeaderChange(event) {
    this._newHeader = event.detail.value;
  }

  handleAddNewHeader() {
    if (!this.isNewHeaderNameValid) {
      return;
    }

    const headers = { ...this._headers };
    headers[this._newHeader] = '';
    this._headers = headers;
    this._newHeader = '';
  }

  /**
   * @param {CustomEvent} event
   */
  handleSaveNameChange(event) {
    this._saveName = event.detail.value;
  }

  handleSave() {
    this.saveState(this._saveName);
    this.requestUpdate();
  }

  /**
   * @param {string} saveName
   * @return {function(): void}
   */
  getHandleLoad(saveName) {
    return () => {
      this.loadState(saveName);
      this._saveName = saveName;
    };
  }

  /**
   * @param {string} saveName
   * @return {function(): void}
   */
  getHandleDeleteSave(saveName) {
    return () => {
      this.deleteSave(saveName);
    };
  }

  handleCopyResponse() {
    navigator.clipboard.writeText(this.responseString);
  }

  /**
   * @param {CustomeEvent} event
   */
  handleDownloadFileNameChange(event) {
    this._downloadFileName = event.detail.value;
  }

  /**
   * Disables shadowRoot for this element.
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   *
   * Calls initial `getData`
   */
  firstUpdated() {
    super.firstUpdated();
    this.getData();
  }

  /**
   * @see OrangeL2Widget.render
   * @returns {TemplateResult}
   */
  render() {
    return super.render({
      renderLoader: () => html`<osk-page-loader is-infinite></osk-page-loader>`,
      renderError: () =>
        html` <osk-error mode="page" has-layout @reload=${this.getData}></osk-error>`,
      renderUi: () => html`
        ${ApiTester.renderHeader()}
        <div class="section bg-gray vertical-stretch">
          <div class="container">
            <div class="grid grid--justify-height">
              <div class="grid__col--12 grid__col--lg-3">
                ${this.renderSubstitutions()}${this.renderSaveLoadSection()}
              </div>
              <div class="grid__col--12 grid__col--lg-9">${this.renderInputSection()}</div>
            </div>
            <div>${this.renderResponse()}</div>
          </div>
        </div>
      `,
    });
  }

  /**
   * @returns {TemplateResult}
   */
  static renderHeader() {
    return html`
      <div class="section section--xsmall bg-black">
        <div class="container">
          <div class="bar">
            <div class="bar__item bar__item--fill">
              <h1 class="display-1">API Tester</h1>
            </div>
            <div class="bar__break hide-lg"></div>
            <div class="bar__item bar__item--shrinkable">
              <div class="bar bar--nowrap">
                <div class="bar__item">
                  <svg class="icon icon--xlarge icon--warning">${unsafeSVG(warningIcon)}</svg>
                </div>
                <div class="bar__item bar__item--shrinkable">
                  <p>
                    <b>POZOR!</b> Táto stránka je prístupná len pre autorizovaných vývojárov.<br />
                    Ak nie ste členom vývojárskeho tímu, okamžite opustite túto stránku.
                  </p>
                </div>
                <div class="bar__item">
                  <svg class="icon icon--xlarge icon--warning">${unsafeSVG(warningIcon)}</svg>
                </div>
              </div>
            </div>
            <div class="bar__item bar__item--fill show-xxl"></div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderSubstitutions() {
    return html`
      <div class="card">
        <div class="card__section card__section--fill">
          <p>Use {key} in URL for a quick substitution.</p>
          <ul>
            ${Object.entries(this.substitutions).map(
              ([key, value]) => html`<li><b>${key}:</b> ${value}</li>`,
            )}
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * @return {TemplateResult}
   */
  renderSaveLoadSection() {
    return html`
      <div class="card">
        <div class="card__section">
          <osk-input
            .value=${this._saveName}
            input-id="save-name"
            label="Save Current Settings"
            is-full-width
            @input-change=${this.handleSaveNameChange}
          >
            <button
              slot="appendInput"
              class="btn btn--primary mb-none"
              ?disabled=${!this._saveName}
              @click=${this.handleSave}
            >
              <svg class="icon">${unsafeSVG(saveIcon)}</svg>
            </button>
          </osk-input>
          <ul>
            ${this._saves.map(
              save =>
                html`<li>
                  <button class="link" @click=${this.getHandleLoad(save)}>${save}</button>
                  <button class="link" @click=${this.getHandleDeleteSave(save)}>
                    <svg class="icon icon--small">${unsafeSVG(trashIcon)}</svg>
                  </button>
                </li>`,
            )}
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderInputSection() {
    return html`
      <div class="card">
        <div class="card__section">
          <div class="grid">
            <div class="grid__col--12 grid__col--lg-2">${this.renderMethodSelect()}</div>
            <div class="grid__col--12 grid__col--lg-10">
              ${this.renderBodyInput()}${this.renderHeadersInput()}
            </div>
          </div>
          ${this.renderUrlInput()}
        </div>
      </div>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderMethodSelect() {
    return html`
      ${METHODS.map(
        method => html`
          <osk-input
            input-id="method-${method}"
            name="method"
            label=${method}
            type="radio"
            value=${method}
            ?checked=${this._method === method}
            @input-change=${this.getHandleMethodChange(method)}
          ></osk-input>
        `,
      )}
      <osk-input
        input-id="custom-headers"
        name="custom-headers"
        label="Custom Headers"
        type="checkbox"
        ?checked=${this._useCustomHeaders}
        @input-change=${this.handleUseCustomHeadersChange}
      ></osk-input>
    `;
  }

  /**
   * @return {?TemplateResult}
   */
  renderHeadersInput() {
    if (!this._useCustomHeaders) {
      return null;
    }
    const headerFields = Object.entries(this._headers).map(
      ([header, value]) => html`
        <osk-input
          .value=${value}
          input-id=${header}
          label=${header}
          is-full-width
          @input-change=${this.getHandleHeaderChange(header)}
        >
          <button
            slot="appendInput"
            class="btn btn--default mb-none"
            @click=${this.getHandleRemoveHeader(header)}
          >
            <svg class="icon">${unsafeSVG(trashIcon)}</svg>
          </button>
        </osk-input>
      `,
    );
    return html`
      <fieldset class="fieldset">
        <legend>Custom Headers</legend>
        ${headerFields}
      </fieldset>

      <osk-input
        .value=${this._newHeader}
        input-id="new-header"
        label="Add New Header"
        error-message=${this._newHeader && !this.isNewHeaderNameValid
          ? 'Invalid header name'
          : null}
        is-full-width
        @input-change=${this.handleNewHeaderChange}
      >
        <button
          slot="appendInput"
          class="btn btn--primary mb-none"
          ?disabled=${!this.isNewHeaderNameValid}
          @click=${this.handleAddNewHeader}
        >
          Add
        </button>
      </osk-input>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderBodyInput() {
    if (!this.isBodyAvailable) {
      return null;
    }

    return html`
      <div class="form-field">
        <div class="form-field__description">
          <label for="body" class="label form-field__label">Body</label>
        </div>
        <textarea
          class="textarea textarea--fullwidth form-field__control ${!this.isBodyValid
            ? 'is-invalid'
            : null}"
          id="body"
          name="body"
          rows="8"
          style="font-family: monospace; font-size: 10pt"
          @change=${this.handleBodyChange}
        >
${this._body}</textarea
        >
        ${!this.isBodyValid
          ? html` <osk-message type=${TYPE_ERROR} .value=${'Invalid JSON'}></osk-message> `
          : null}
      </div>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderUrlInput() {
    return html`
      <osk-input
        .value=${this._url}
        input-id="url"
        label="URL"
        is-full-width
        ?is-valid=${this.isUrlValid}
        error-message=${!this.isUrlValid && this.substitutedUrl ? 'Invalid URL' : null}
        @input-change=${this.handleUrlChange}
      ></osk-input>
      ${this._url !== this.substitutedUrl
        ? html`
            <osk-input
              .value=${this.substitutedUrl}
              input-id="substituted-url"
              label="Substituted URL"
              is-full-width
              is-disabled
            ></osk-input>
          `
        : null}
      <div>
        <button
          class="btn btn--primary"
          ?disabled=${!this.isUrlValid || (this._body && !this.isBodyValid)}
          @click=${this.handleTestClick}
        >
          Test
        </button>
      </div>
    `;
  }

  /**
   * @returns {TemplateResult}
   */
  renderResponse() {
    return html`
      <div class="form-field">
        <div class="form-field__description">
          <label for="response" class="label form-field__label">Response / Error</label>
        </div>
        <textarea
          class="textarea textarea--fullwidth form-field__control ${this._hasError
            ? 'is-invalid'
            : null}"
          id="response"
          name="response"
          rows="20"
          style="font-family: monospace; font-size: 10pt; color: black"
          readonly
        >
${this.responseString}</textarea
        >
      </div>
      <button
        class="btn btn--primary"
        ?disabled=${!this.responseString}
        @click=${this.handleCopyResponse}
      >
        Copy to Clipboard
      </button>
      <osk-input
        style="display: inline-block"
        .value=${this._downloadFileName}
        input-id="file-name"
        placeholder="api-tester-response"
        @input-change=${this.handleDownloadFileNameChange}
      >
        <a
          slot="prependInput"
          class="btn btn--primary mb-none"
          ?disabled=${!this.responseString}
          href="data:application/json;charset=utf-8,${this.responseString}"
          download="${this._downloadFileName || 'api-tester-response'}.json"
          >Download</a
        >
      </osk-input>
    `;
  }
}

export default ApiTester;
